import { useRef, useEffect, useState } from 'react';

/**
 * @typedef {Object} NewsItem
 * @property {string} title - The title of the news item
 * @property {string} source - The source of the news item
 * @property {string} url - The URL to the full article
 */

/**
 * @typedef {Object} NewsCarouselProps
 * @property {NewsItem[]} items - Array of news items to display
 * @property {number} [autoScrollInterval=5000] - Time in ms between auto-scrolls
 * @property {boolean} [pauseOnHover=true] - Whether to pause auto-scroll on hover
 */

/**
 * A carousel component for displaying news items with auto-scrolling functionality
 * @param {NewsCarouselProps} props
 * @returns {JSX.Element}
 */
export default function NewsCarousel({ 
  items, 
  autoScrollInterval = 5000, // Default to 5 seconds between scrolls
  pauseOnHover = true 
}) {
  const containerRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);

  // Calculate the exact width of one item (including gap)
  const getItemWidth = () => {
    if (containerRef.current) {
      // Each item is 3/4 of screen width (w-3/4) + gap of 4 = container width + 4
      return containerRef.current.clientWidth + 4;
    }
    return 768; // Default if ref not available (approximation for 3/4 of common screen width)
  };

  const scrollByOffset = (offset) => {
    if (containerRef.current) {
      // Check if we've reached the end and need to reset
      const container = containerRef.current;
      const itemWidth = getItemWidth();
      const isAtEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 10;

      if (isAtEnd && offset > 0) {
        // Reset to beginning with smooth animation
        container.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        // Scroll by exactly one item width
        container.scrollBy({ left: offset > 0 ? itemWidth : -itemWidth, behavior: 'smooth' });
      }
    }
  };

  // Ensure first item is visible when component mounts
  useEffect(() => {
    if (containerRef.current) {
      // Reset to the beginning when component mounts
      containerRef.current.scrollTo({ left: 0, behavior: 'auto' });
    }
  }, []);

  // Auto-scroll functionality
  useEffect(() => {
    if (items.length <= 1 || !autoScrollInterval) return;

    let intervalId;

    if (!isPaused) {
      intervalId = setInterval(() => {
        scrollByOffset(1); // Scroll forward by one item
      }, autoScrollInterval);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [items.length, autoScrollInterval, isPaused]);

  return (
    <div className="relative">
      {/* Previous button */}
      <button
        onClick={() => scrollByOffset(-1)}
        aria-label="Previous"
        className="absolute left-0 top-1/2 z-10 -translate-y-1/2 p-2 bg-white/70 rounded-full shadow hover:bg-white transition"
      >
        ‹
      </button>

      {/* Carousel container */}
      <div
        ref={containerRef}
        role="list"
        className="flex gap-4 overflow-x-hidden w-3/4 mx-auto scroll-pl-4 snap-x snap-mandatory scroll-smooth py-4"
        onMouseEnter={() => pauseOnHover && setIsPaused(true)}
        onMouseLeave={() => pauseOnHover && setIsPaused(false)}
      >
        {items.map((item, index) => (
          <a
            key={index}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            role="listitem"
            className="snap-start flex-shrink-0 w-full p-6 md:p-8 bg-white rounded-lg border border-gray-200 shadow-md hover:shadow-xl transition-all"
          >
            <h3 className="text-xl md:text-2xl font-semibold mb-2">{item.title}</h3>
            <p className="text-base text-gray-500">{item.source}</p>
          </a>
        ))}
      </div>

      {/* Next button */}
      <button
        onClick={() => scrollByOffset(1)}
        aria-label="Next"
        className="absolute right-0 top-1/2 z-10 -translate-y-1/2 p-2 bg-white/70 rounded-full shadow hover:bg-white transition"
      >
        ›
      </button>
    </div>
  );
}
